import { useState, useEffect } from 'react';
import { X, Check, XIcon } from 'lucide-react';
import { Transaction } from '../../types/dashboard';
import { FormData } from '../../types/transaction';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useEmailNotifications } from '../../hooks/useEmailNotifications';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import BasicInfoForm from '../transaction/forms/BasicInfoForm';
import AgenciesForm from '../transaction/forms/AgenciesForm';
import NegotiatorsForm from '../transaction/forms/NegotiatorsForm';
import AssistantsForm from '../transaction/forms/AssistantsForm';
import FinancialForm from '../transaction/forms/FinancialForm';
import RetrocessionsForm from '../transaction/forms/RetrocessionsForm';
import { checkRetrocessionsAgainstNegotiators } from '../../utils/retrocessions';

interface ViewRegulModalProps {
  transaction: Transaction | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ViewRegulModal({ transaction, isOpen, onClose }: ViewRegulModalProps) {
  const { user } = useAuth();
  const { sendRegularizationApprovalNotification } = useEmailNotifications();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [adminComment, setAdminComment] = useState('');
  const [regulData, setRegulData] = useState<any>(null);
  const [formData, setFormData] = useState<FormData>({
    property_number: '',
    mandate_type: 'simple',
    mandate_date: '',
    property_type: '',
    property_origin: '',
    lead_origin: '',
    seller: '',
    buyer: '',
    sale_price: '',
    fees_with_tax: '',
    fees_without_tax: '',
    agencies: [],
    negotiators: [],
    assistants: [],
    retrocessions: []
  });

  useEscapeKey(() => {
    if (isOpen) {
      onClose();
    }
  });

  useEffect(() => {
    if (transaction?.id && transaction.regul) {
      loadRegulData();
    }
  }, [transaction]);

  const loadRegulData = async () => {
    if (!transaction?.id) return;
    
    try {
      setLoading(true);

      // Charger la demande de régularisation
      const { data: regulRequests, error: regulError } = await supabase
        .from('regul_requests')
        .select(`
          *,
          user:requested_by (
            firstname,
            lastname
          )
        `)
        .eq('transaction_id', transaction.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1);

      if (regulError) throw regulError;
      
      if (regulRequests && regulRequests.length > 0) {
        setRegulData(regulRequests[0]);

        // Charger les détails complets de la transaction
        const { data: transactionData, error: transactionError } = await supabase
          .from('transactions')
          .select(`
            *,
            transaction_agencies (
              agency
            ),
            transaction_negotiators (
              negotiator:negotiators (
                id,
                firstname,
                lastname
              ),
              role,
              percentage,
              agency
            ),
            transaction_assistants (
              assistant:assistants (
                id,
                firstname,
                lastname
              ),
              role
            ),
            transaction_retrocessions (
              id,
              type,
              amount,
              description,
              payment_date,
              primary_negotiator_id,
              secondary_negotiator_id,
              tertiary_negotiator_id,
              quaternary_negotiator_id,
              distribution_type,
              distribution_percentages
            )
          `)
          .eq('id', transaction.id)
          .single();

        if (transactionError) throw transactionError;

        // Formater les données pour le formulaire
        setFormData({
          property_number: transactionData.property_number,
          mandate_type: transactionData.mandate_type || 'simple',
          mandate_date: transactionData.mandate_date || '',
          property_type: transactionData.property_type || '',
          property_origin: transactionData.property_origin || '',
          lead_origin: transactionData.lead_origin || '',
          seller: transactionData.seller,
          buyer: transactionData.buyer,
          sale_price: transactionData.sale_price.toString(),
          fees_with_tax: transactionData.fees_with_tax.toString(),
          fees_without_tax: transactionData.fees_without_tax.toString(),
          agencies: transactionData.transaction_agencies?.map(ta => ({ 
            name: ta.agency 
          })) || [],
          negotiators: transactionData.transaction_negotiators?.map(tn => ({
            id: tn.negotiator.id,
            name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
            role: tn.role,
            percentage: tn.percentage,
            agency: tn.agency || ''
          })) || [],
          assistants: transactionData.transaction_assistants?.map(ta => ({
            id: ta.assistant.id,
            role: ta.role
          })) || [],
          retrocessions: transactionData.transaction_retrocessions?.map(tr => ({
            type: tr.type,
            amount: tr.amount.toString(),
            description: tr.description || '',
            payment_date: tr.payment_date || null,
            distribution_type: tr.distribution_type || 'single',
            primary_negotiator_id: tr.primary_negotiator_id || '',
            secondary_negotiator_id: tr.secondary_negotiator_id || null,
            tertiary_negotiator_id: tr.tertiary_negotiator_id || null,
            quaternary_negotiator_id: tr.quaternary_negotiator_id || null,
            distribution_percentages: tr.distribution_percentages
          })) || []
        });
      }
    } catch (err) {
      console.error('Error loading regul data:', err);
      setError('Erreur lors du chargement de la demande');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!transaction || !regulData || !user) return;

    try {
      setLoading(true);

      // Garde-fou : les négociateurs des rétrocessions doivent être ceux de la vente
      const retrocessionProblems = checkRetrocessionsAgainstNegotiators(
        formData.retrocessions,
        formData.negotiators.filter(n => n.id)
      );
      if (retrocessionProblems.length > 0) {
        throw new Error(retrocessionProblems.join(' '));
      }

      // Mettre à jour la transaction avec les nouvelles valeurs
      const transactionData = {
        property_number: formData.property_number,
        mandate_type: formData.mandate_type,
        mandate_date: formData.mandate_date || null,
        property_type: formData.property_type || null,
        property_origin: formData.property_origin || null,
        lead_origin: formData.lead_origin || null,
        seller: formData.seller,
        buyer: formData.buyer,
        sale_price: parseFloat(formData.sale_price),
        fees_with_tax: parseFloat(formData.fees_with_tax),
        fees_without_tax: parseFloat(formData.fees_without_tax),
        regul: false,
        commentaire_regul: null
      };

      const { error: updateError } = await supabase
        .from('transactions')
        .update(transactionData)
        .eq('id', transaction.id);

      if (updateError) throw updateError;

      // Mettre à jour les agences
      await supabase
        .from('transaction_agencies')
        .delete()
        .eq('transaction_id', transaction.id);

      await supabase
        .from('transaction_agencies')
        .insert(
          formData.agencies
            .filter(a => a.name)
            .map(a => ({
              transaction_id: transaction.id,
              agency: a.name
            }))
        );

      // Mettre à jour les négociateurs
      await supabase
        .from('transaction_negotiators')
        .delete()
        .eq('transaction_id', transaction.id);

      await supabase
        .from('transaction_negotiators')
        .insert(
          formData.negotiators
            .filter(n => n.id)
            .map(n => {
              // Conserver l'agence : sans cela, valider une régularisation effaçait
              // l'agence de chaque négociateur de la transaction
              let agency = n.agency;
              if (!agency && formData.agencies.length === 1 && formData.agencies[0].name) {
                agency = formData.agencies[0].name;
              }
              return {
                transaction_id: transaction.id,
                negotiator_id: n.id,
                role: n.role,
                percentage: n.percentage,
                agency: agency || null
              };
            })
        );

      // Mettre à jour les assistantes
      await supabase
        .from('transaction_assistants')
        .delete()
        .eq('transaction_id', transaction.id);

      await supabase
        .from('transaction_assistants')
        .insert(
          formData.assistants
            .filter(a => a.id)
            .map(a => ({
              transaction_id: transaction.id,
              assistant_id: a.id,
              role: a.role
            }))
        );

      // Mettre à jour les rétrocessions
      await supabase
        .from('transaction_retrocessions')
        .delete()
        .eq('transaction_id', transaction.id);

      if (formData.retrocessions.length > 0) {
        await supabase
          .from('transaction_retrocessions')
          .insert(
            formData.retrocessions.map(r => ({
              transaction_id: transaction.id,
              type: r.type,
              amount: parseFloat(r.amount),
              description: r.description || null,
              payment_date: r.payment_date || null,
              primary_negotiator_id: r.primary_negotiator_id,
              secondary_negotiator_id: r.secondary_negotiator_id || null,
              tertiary_negotiator_id: r.tertiary_negotiator_id || null,
              quaternary_negotiator_id: r.quaternary_negotiator_id || null,
              distribution_type: r.distribution_type,
              distribution_percentages: r.distribution_percentages || null
            }))
          );
      }

      // Mettre à jour le statut de la demande
      const { error: requestError } = await supabase
        .from('regul_requests')
        .update({
          status: 'approved',
          admin_comment: adminComment,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', regulData.id);

      if (requestError) throw requestError;

      // Mettre à jour le statut de régularisation à "approuvée"
      const { error: statusError } = await supabase
        .from('transactions')
        .update({ regul_status: 2 })
        .eq('id', transaction.id);

      if (statusError) throw statusError;

      // Envoyer une notification email au demandeur
      try {
        await sendRegularizationApprovalNotification(transaction.id, true, adminComment);
      } catch (emailError) {
        console.error('Error sending approval notification:', emailError);
        // Ne pas faire échouer l'approbation si l'email échoue
      }

      // Ajouter le log
      await supabase
        .from('transaction_logs')
        .insert([{
          transaction_id: transaction.id,
          user_id: user.id,
          event_type: 'regul_request',
          event_data: {
            action: 'approved',
            comment: adminComment
          }
        }]);

      onClose();
      window.location.reload();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!transaction || !regulData || !user) return;

    try {
      setLoading(true);

      const { error: updateError } = await supabase
        .from('transactions')
        .update({
          regul: false,
          commentaire_regul: null
        })
        .eq('id', transaction.id);

      if (updateError) throw updateError;

      // Mettre à jour le statut de la demande
      const { error: requestError } = await supabase
        .from('regul_requests')
        .update({
          status: 'rejected',
          admin_comment: adminComment,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', regulData.id);

      if (requestError) throw requestError;

      // Mettre à jour le statut de régularisation à "rejetée"
      const { error: statusError } = await supabase
        .from('transactions')
        .update({ regul_status: 3 })
        .eq('id', transaction.id);

      if (statusError) throw statusError;

      // Envoyer une notification email au demandeur
      try {
        await sendRegularizationApprovalNotification(transaction.id, false, adminComment);
      } catch (emailError) {
        console.error('Error sending rejection notification:', emailError);
        // Ne pas faire échouer le rejet si l'email échoue
      }

      await supabase
        .from('transaction_logs')
        .insert([{
          transaction_id: transaction.id,
          user_id: user.id,
          event_type: 'regul_request',
          event_data: {
            action: 'rejected',
            comment: adminComment
          }
        }]);

      onClose();
      window.location.reload();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !transaction) return null;

  if (!regulData) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200]">
        <div className="bg-white rounded-xl p-6">
          <h2 className="text-xl font-bold mb-4">Aucune demande trouvée</h2>
          <p className="text-gray-600 mb-4">
            Aucune demande de régularisation en attente n'a été trouvée pour cette transaction.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Fermer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200]">
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 w-full max-w-4xl relative max-h-[90vh] overflow-y-auto shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={20} />
        </button>

        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Demande de régularisation - Transaction #{transaction.property_number}
            </h2>
            <div className="mt-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-100 shadow-sm">
              <p className="font-medium text-gray-900">
                Demandé par : {regulData.user?.firstname} {regulData.user?.lastname}
              </p>
              <p className="text-gray-500 text-sm">
                Le {new Date(regulData.created_at).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              <p className="mt-2 text-red-600 font-medium">
                Motif : {regulData.request_comment}
              </p>
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Informations générales</h3>
              <BasicInfoForm
                formData={formData}
                onChange={setFormData}
                currentTransactionId={transaction?.id}
              />
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-purple-800 mb-4">Agences</h3>
              <AgenciesForm
                formData={formData}
                onChange={setFormData}
              />
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-green-800 mb-4">Négociateurs</h3>
              <NegotiatorsForm
                formData={formData}
                onChange={setFormData}
              />
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-amber-800 mb-4">Assistantes</h3>
              <AssistantsForm
                formData={formData}
                onChange={setFormData}
              />
            </div>

            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-teal-800 mb-4">Informations financières</h3>
              <FinancialForm
                formData={formData}
                onChange={setFormData}
              />
            </div>

            <div className="bg-gradient-to-r from-rose-50 to-red-50 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-rose-800 mb-4">Rétrocessions</h3>
              <RetrocessionsForm
                formData={formData}
                onChange={setFormData}
              />
            </div>

            <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Commentaire administrateur</h3>
              <textarea
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                rows={4}
                placeholder="Ajoutez un commentaire (optionnel)..."
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
            >
              Fermer
            </button>
            <button
              type="button"
              onClick={handleReject}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg hover:from-red-600 hover:to-red-700 disabled:opacity-50 transition-all duration-200 shadow-sm"
            >
              <XIcon size={16} />
              <span>{loading ? 'Chargement...' : 'Rejeter'}</span>
            </button>
            <button
              type="button"
              onClick={handleApprove}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 transition-all duration-200 shadow-sm"
            >
              <Check size={16} />
              <span>{loading ? 'Chargement...' : 'Approuver'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}